import { intersection, partition } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BillingRoutes } from "../../pages/billing/routes";
import { BlockchainToolsRoutes } from "../../pages/blockchain-tools/routes";
import { DashboardRoutes } from "../../pages/dashboard/routes";
import { MerchantDueDiligenceRoutes } from "../../pages/due-diligence/routes";
import { FraudRoutes } from "../../pages/kyc-kyb-aml/routes";
import { LaunchConfigurationsRoutes } from "../../pages/launch-configurator/routes";
import { MerchantsRoutes } from "../../pages/merchants/routes";
import { ParticipationsRoutes } from "../../pages/participations/routes";
import { PasswordManagerRoutes } from "../../pages/password-manager/routes";
import { ProfileRoutes } from "../../pages/profile/routes";
import { SettingsRoutes } from "../../pages/settings/routes";
import { StakingRoutes } from "../../pages/staking/routes";
// import { TokenOnboardingRoutes } from "../../pages/token-onboarding/routes";
import { ToolsRoutes } from "../../pages/tools/routes";
import { UsersRoutes } from "../../pages/users/routes";
import { VaultsRoutes } from "../../pages/vaults/routes";
import { WalletsRoutes } from "../../pages/wallets/routes";
import { AppStore } from "../../store";
import { AuthDTO } from "../../store/auth/models/dto";
import { Fetchable, isFetchableSuccess } from "../../store/common/fetchable/helpers";
import { MenuNode } from "../menu";
import { ELearningCouponsRoutes } from "../../pages/coupons/routes";
import { useBrowserWallet } from "../../store/wallets/crypto";
import { Option } from "space-monad";
import BN from "bn.js";
// import { SubscriptionRoutes } from "../../pages/subscription/routes";
import { useFetchable } from "../../store/common/fetchable";
import { Users } from "../../store/users/reducer";
import { StakeContext } from "../../pages/staking/single/context";
import { Wallets } from "../../store/wallets/reducer";
import { KYCPacksRoutes } from "../../pages/kyc-packs/routes";

const hundredThousand = new BN('152D02C7E14AF6800000', 16);
const zero = new BN('0', 16);


const useUserPermissions = () => {
  const authFetchable = useSelector<
    AppStore,
    Fetchable<AuthDTO.Responses.Login.T>
  >((state) => state.auth.login);
  return useMemo(
    () =>
      authFetchable.status === "ready"
        ? authFetchable.data.accessToken.roles
        : undefined,
    [authFetchable]
  );
};




const useUserSidebar = () => {
  const [flag, setFlag] = useState<boolean>(false);
  const [hasStaked, setHasStaked] = useState<boolean>(false);
  const {
    connect,
    verify,
    state,
    balance,
    accounts,
    isVerified,
  } = useBrowserWallet();

  const currentUser = useFetchable(Users.current);
  const totalStakedbalance = useFetchable(Wallets.totalBalance);

  useEffect(() => {


    // Update the document title using the browser API

    accounts.fold(
      () => (
        setFlag(false)
      ), () => (
        Option.all([balance, accounts]).fold(
          () => null,
          ([b, a]) => (
            setFlag(b.amount.gte(hundredThousand))
          )
        )
      ))


    if (isFetchableSuccess(totalStakedbalance)) {
      if (totalStakedbalance.data) {
        setFlag(totalStakedbalance.data >= 100000)
        setHasStaked(totalStakedbalance.data > 0)
      }
    }


    if (isFetchableSuccess(currentUser)) {
      if (currentUser.data.payingSubscriber) {
        setFlag(true)
      }
    }
    if (process.env.REACT_APP_ENABLE_REWARDS_PROGRAM === 'true') {
      setHasStaked(true);
    }
  }, [balance, accounts, currentUser, totalStakedbalance]);

  const userPermissions = useUserPermissions();


  const tools = ToolsRoutes.useSidebar();
  return useMemo(
    () => {
      return [
        DashboardRoutes.sidebar,
        ProfileRoutes.sidebar,
        ...[
          // SubscriptionRoutes.sidebar,
          // KYCPacksRoutes.sidebar,
          ELearningCouponsRoutes.sidebar,
          WalletsRoutes.sidebar
        ]
          .filter(
            (x) => (
              ((userPermissions?.includes("ROLE_USER") && flag) || (!userPermissions?.includes("ROLE_USER"))) && (x.roles === undefined || (userPermissions && x.roles.exec(userPermissions)))
            )),
        ...(hasStaked ? [StakingRoutes.sidebar] : []), // Rewards Program
        ...[
          ParticipationsRoutes.adminSidebar,
          UsersRoutes.sidebar,
          MerchantsRoutes.sidebar,

          ParticipationsRoutes.defaultSidebar, // My Onboarded Projects

          // TokenOnboardingRoutes.sidebar, // Projects

          FraudRoutes.kycSidebar,
          FraudRoutes.kybSidebar,

          BlockchainToolsRoutes.sidebar,

          BillingRoutes.sidebar,
          BillingRoutes.adminSidebar,
        ]
          .filter(
            (x) => (
              ((userPermissions?.includes("ROLE_USER") && flag) || (!userPermissions?.includes("ROLE_USER"))) && (x.roles === undefined || (userPermissions && x.roles.exec(userPermissions)))
            )),
        SettingsRoutes.sidebar,
        ...[
          tools,
          VaultsRoutes.sidebar,
          PasswordManagerRoutes.sidebar,
        ]
          //     Dashboard
          //     ii.     	My Profile
          //  iii.     	My e-Leaning Coupons
          //  iv.     	My Web3 Wallets
          //     v.     	My Onboarded Projects
          //  vi.     	Report a Scam
          .filter(
            (x) => (
              ((userPermissions?.includes("ROLE_USER") && flag) || (!userPermissions?.includes("ROLE_USER"))) && (x.roles === undefined || (userPermissions && x.roles.exec(userPermissions)))
            ))];

    },
    [flag, hasStaked, tools, userPermissions]
  );
};

const useMerchantSidebar = () => {
  const userPermissions = useUserPermissions();
  const tools = ToolsRoutes.useSidebar();
  return useMemo(
    () => <MenuNode.MenuNode[]>[
      ParticipationsRoutes.adminSidebar,
      {
        icon: "kycMenu",
        title: "know-your-client",
        children: [
          {
            icon: "kycNew",
            route: FraudRoutes.states[0],
          },
          {
            icon: "amlIndividualNew",
            route: FraudRoutes.states[7],
          },
          {
            icon: "kycAll",
            route: FraudRoutes.states[2],
          },
          {
            icon: "amlIndividualAll",
            route: FraudRoutes.states[8],
          },
          {
            icon: "rocket",
            route: LaunchConfigurationsRoutes.states[0],
          },
          {
            icon: "rocket",
            route: LaunchConfigurationsRoutes.states[2],
          },
        ],
      },
      {
        icon: "kybMenu",
        title: "know-your-business",
        children: [
          {
            icon: "kybNew",
            route: FraudRoutes.states[3],
          },
          {
            icon: "amlBusinessNew",
            route: FraudRoutes.states[9],
          },
          {
            icon: "kybAll",
            route: FraudRoutes.states[4],
          },
          {
            icon: "amlBusinessAll",
            route: FraudRoutes.states[10],
          },
          {
            icon: "cryptoToken",
            route: MerchantDueDiligenceRoutes.states[0],
          },
          {
            icon: "cryptoToken",
            route: MerchantDueDiligenceRoutes.states[2],
          },
        ],
      },

      BlockchainToolsRoutes.sidebar,
      BillingRoutes.sidebar,
      SettingsRoutes.sidebar,
      tools,
    ].filter(
      (x) =>
        (x as any).roles === undefined ||
        (userPermissions && (x as any).roles.exec(userPermissions))
    ),
    [tools, userPermissions]
  );
};

export const useSidebarList: () => MenuNode.MenuNode[] = () => {
  const userPermissions = useUserPermissions();

  const merchantSidebar = useMerchantSidebar();
  const userSidebar = useUserSidebar();

  return useMemo(
    () =>
      userPermissions?.includes("ROLE_MERCHANT")
        ? merchantSidebar
        : userSidebar,
    [userPermissions, merchantSidebar, userSidebar]
  );
};
